import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState, type ReactNode } from 'react'

/**
 * Small presentational primitives sized for a ~350px Outlook task pane.
 *
 * bol-ui's own components are built for the full-width BOL3 workbench, so the pane
 * uses these compact equivalents while taking its colours and spacing from the
 * shared bol-ui theme tokens.
 */

export function Card({ children }: { children: ReactNode }) {
    return <div className="mb-2 overflow-hidden rounded-md border border-border bg-card">{children}</div>
}

export function Section({
    title,
    count,
    defaultOpen = true,
    children,
}: {
    title: string
    count?: string | number
    defaultOpen?: boolean
    children: ReactNode
}) {
    const [open, setOpen] = useState(defaultOpen)

    return (
        <Card>
            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
                className={`flex w-full items-center gap-1.5 px-2.5 py-2 text-left text-[11.5px] font-semibold ${
                    open ? 'border-b border-border' : ''
                }`}
            >
                <span className="text-foreground/50">
                    {open ? <ChevronDown size={11} strokeWidth={3} /> : <ChevronRight size={11} strokeWidth={3} />}
                </span>
                {title}
                {count !== undefined && (
                    <span className="ml-auto rounded-full bg-background px-1.5 py-px text-[10px] font-semibold text-foreground/55">
                        {count}
                    </span>
                )}
            </button>
            {open && <div className="px-2.5 py-2">{children}</div>}
        </Card>
    )
}

type PillTone = 'red' | 'amber' | 'green' | 'blue' | 'grey'

const PILL_TONES: Record<PillTone, string> = {
    red: 'bg-destructive/15 text-[hsl(4_72%_38%)]',
    amber: 'bg-accent/20 text-[hsl(30_84%_31%)]',
    green: 'bg-success/[0.17] text-[hsl(145_60%_25%)]',
    blue: 'bg-action/15 text-[hsl(203_70%_29%)]',
    grey: 'bg-background text-foreground/55',
}

export function Pill({ tone = 'grey', children }: { tone?: PillTone; children: ReactNode }) {
    return (
        <span
            className={`inline-block whitespace-nowrap rounded-full px-1.5 py-px text-[10px] font-semibold ${PILL_TONES[tone]}`}
        >
            {children}
        </span>
    )
}

/** A bordered list row with a main column and an optional right-hand column. */
export function Row({ main, aside }: { main: ReactNode; aside?: ReactNode }) {
    return (
        <div className="flex items-start gap-2 border-t border-border py-1.5 first:border-t-0 first:pt-0.5">
            <div className="min-w-0 flex-1">{main}</div>
            {aside && <div className="flex-none text-right">{aside}</div>}
        </div>
    )
}

export function RowTitle({ children }: { children: ReactNode }) {
    return <div className="truncate text-[11.5px] font-semibold">{children}</div>
}

export function RowMeta({ children }: { children: ReactNode }) {
    return <div className="mt-px text-[10.5px] text-foreground/55">{children}</div>
}

export function KeyValue({ rows }: { rows: [label: string, value: ReactNode][] }) {
    return (
        <dl className="grid grid-cols-[82px_1fr] gap-x-2 gap-y-0.5 text-[11.5px]">
            {rows.map(([label, value]) => (
                <div key={label} className="contents">
                    <dt className="text-foreground/55">{label}</dt>
                    <dd className="m-0 font-medium break-words">{value}</dd>
                </div>
            ))}
        </dl>
    )
}

export function Button({
    variant = 'accent',
    type = 'button',
    disabled,
    onClick,
    children,
}: {
    variant?: 'accent' | 'outline'
    type?: 'button' | 'submit'
    disabled?: boolean
    onClick?: () => void
    children: ReactNode
}) {
    // `accent` mirrors bol-ui's Button `default` variant (bg-accent / text-accent-foreground).
    const styles =
        variant === 'accent'
            ? 'bg-accent text-accent-foreground hover:bg-accent/90'
            : 'border border-border bg-card hover:bg-background'

    return (
        <button
            type={type}
            disabled={disabled}
            onClick={onClick}
            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50 ${styles}`}
        >
            {children}
        </button>
    )
}

export function CentredState({
    icon,
    title,
    body,
    children,
    fill = false,
}: {
    icon: ReactNode
    title: string
    body?: ReactNode
    children?: ReactNode
    fill?: boolean
}) {
    return (
        <div
            className={`flex flex-col items-center justify-center gap-3 px-5 py-6 text-center ${
                fill ? 'flex-1' : ''
            }`}
        >
            <div className="grid size-12 place-items-center rounded-xl border border-border bg-card text-primary">
                {icon}
            </div>
            <h2 className="m-0 text-sm font-semibold">{title}</h2>
            {body && <p className="m-0 max-w-[34ch] text-[11.5px] leading-relaxed text-foreground/55">{body}</p>}
            {children}
        </div>
    )
}

export function Skeleton({ width = '100%', tall = false }: { width?: string; tall?: boolean }) {
    return (
        <div
            className={`animate-pulse rounded bg-background ${tall ? 'h-3' : 'h-2'}`}
            style={{ width }}
            aria-hidden
        />
    )
}

export function ErrorNotice({ message, onRetry }: { message: string; onRetry?: () => void }) {
    return (
        <Card>
            <div className="px-2.5 py-2">
                <div className="text-[11.5px] font-semibold text-[hsl(4_72%_38%)]">Something went wrong</div>
                <p className="mt-1 mb-2 text-[10.5px] text-foreground/60">{message}</p>
                {onRetry && (
                    <Button variant="outline" onClick={onRetry}>
                        Try again
                    </Button>
                )}
            </div>
        </Card>
    )
}
